import type { JSX } from 'react';
import { ContactEmailLink, DataTable, Section } from './privacyPrimitives';

const CONTACT_ROWS = [
  ['Candidate', 'Consent', 'Art. 6(1)(a)'],
  [
    'Reference contact',
    'Legitimate interest, with consent for recorded statements',
    'Art. 6(1)(f) and 6(1)(a)',
  ],
] as const;

const RETENTION_ROWS = [
  ['Candidate profile', 'Until you ask us to delete it', 'Data minimisation'],
  [
    'Verifications',
    'While the company that requested them needs the record',
    'Legitimate interest',
  ],
  ['Reference contact details', 'While the verification record is kept', 'Legitimate interest'],
  [
    'Consent and audit records',
    'As long as we must show when consent was given',
    'Legal obligation',
  ],
] as const;

const RIGHTS = [
  ['Access', 'Art. 15'],
  ['Rectification', 'Art. 16'],
  ['Erasure', 'Art. 17'],
  ['Restriction of processing', 'Art. 18'],
  ['Data portability', 'Art. 20'],
  ['Objection', 'Art. 21'],
  ['Rights related to automated decisions', 'Art. 22'],
] as const;

export function ControllerSection(): JSX.Element {
  return (
    <Section title="Who is responsible for your data">
      <p>
        Career Verification (&ldquo;we&rdquo;) is the controller of the personal data described in
        this notice. For any question about this notice or about your data, contact us at{' '}
        <ContactEmailLink />.
      </p>
    </Section>
  );
}

export function CollectedDataSection(): JSX.Element {
  return (
    <Section title="What data we collect">
      <p>For candidates we collect:</p>
      <ul className="mt-2 list-disc space-y-1 pl-6">
        <li>full name, email address, phone number and country</li>
        <li>work history: job title, company, employment period</li>
        <li>education records: institution, field, city, country and years attended</li>
        <li>contact details of the reference contacts you provide</li>
        <li>verification results and the timestamps of your consents</li>
      </ul>
      <p className="mt-4">For reference contacts we collect:</p>
      <ul className="mt-2 list-disc space-y-1 pl-6">
        <li>full name, email address, phone number, job title and relationship to the candidate</li>
        <li>your answers to our verification questions</li>
        <li>your confirmation or dispute of the candidate&apos;s employment details</li>
        <li>any comment you choose to leave</li>
      </ul>
    </Section>
  );
}

export function LegalBasisSection(): JSX.Element {
  return (
    <Section title="Why we process your data">
      <p>
        We process personal data to check employment history, to show verification results to the
        parties involved, and to keep an audit trail of consents and verifications.
      </p>
      <DataTable
        caption="Legal bases for processing per data subject"
        headers={['Data subject', 'Legal basis', 'GDPR article']}
        rows={CONTACT_ROWS}
      />
      <p className="mt-4">
        You can withdraw your consent at any time. Withdrawal does not affect processing that
        already took place before it.
      </p>
    </Section>
  );
}

export function RecipientsSection(): JSX.Element {
  return (
    <Section title="Who receives your data">
      <p>
        Verification results are shown to the candidate whose history is being checked and to the
        company that invited the candidate. We use service providers for hosting, email delivery and
        error monitoring. They process data on our instructions only.
      </p>
      <p className="mt-4">
        We also publish a shareable certificate once a verification has been accepted. Anyone who
        holds its link can open it without signing in. The certificate shows the candidate&apos;s
        name, country and confirmed work history, together with the reference contact&apos;s name,
        job title, confirmation and any comment they left. Do not share the link with people you do
        not want to see that information.
      </p>
    </Section>
  );
}

export function RetentionSection(): JSX.Element {
  return (
    <Section title="How long we keep your data">
      <DataTable
        caption="Data retention periods"
        headers={['Data category', 'Retention period', 'Reason']}
        rows={RETENTION_ROWS}
      />
    </Section>
  );
}

export function RightsSection(): JSX.Element {
  return (
    <Section title="Your rights">
      <p>Under the GDPR you have the following rights regarding your personal data:</p>
      <ul className="mt-2 list-disc space-y-1 pl-6">
        {RIGHTS.map(([right, article]) => (
          <li key={right}>
            {right} ({article})
          </li>
        ))}
      </ul>
      <p className="mt-4">
        We do not make decisions about you based on solely automated processing or profiling, so the
        right under Art. 22 does not apply to our processing today.
      </p>
      <p className="mt-4">
        To exercise any of these rights, write to <ContactEmailLink />. You also have the right to
        lodge a complaint with a supervisory authority.
      </p>
    </Section>
  );
}

export function CookiesSection(): JSX.Element {
  return (
    <Section title="Cookies and analytics">
      <p>
        We use Microsoft Clarity to understand how the application is used. Analytics cookies are
        set only after you consent to them. We do not use advertising cookies.
      </p>
      <p className="mt-4">
        To withdraw your analytics consent, write to <ContactEmailLink />. We keep a record of your
        withdrawal and handle it manually. A settings screen where you can withdraw consent yourself
        is not available yet.
      </p>
    </Section>
  );
}
